import React from 'react';
import { Column } from 'primereact/column';
import { t } from 'i18next';
import CustomTooltip from '../components/common/tooltip/CustomTooltip';
import { LinksData } from '../../pages/prompt/preview/components/stats/models/StatsModel';
import { getMonitorTypeIcon } from '../../pages/prompt/preview/components/stats/helpers/StatsHelper';
import { getMonitorName } from './MonitorTypeHelpers';
import { Tooltip } from 'primereact/tooltip';
import { v4 as uuidv4 } from 'uuid';

interface CustomColumnProps {
  sortable?: boolean;
  field?: string;
  /** Sort by a different field than `field` (e.g. a numeric twin for a display-formatted string column). */
  sortField?: string;
  header?: any;
  headerName?: string;
  headerNameSuffix?: string;
  headerClassName?: string;
  bodyClassName?: string;
  body?: any;
  style?: React.CSSProperties;
  hidden?: boolean;
  tooltip?: string;
}

export const createCustomColumn = ({
  sortable,
  field,
  sortField,
  header,
  headerName,
  tooltip,
  style,
  hidden,
  body,
  bodyClassName,
  headerClassName,
  headerNameSuffix = '',
}: CustomColumnProps): JSX.Element => {
  const id = `table-header-${sanitizeId(headerName || '')}`;
  const finalHeader =
    !tooltip || !headerName ? (
      header
    ) : (
      <>
        <span id={id} className="table__header">
          {t(headerName) + headerNameSuffix}
        </span>
        <CustomTooltip tooltipText={tooltip} targetRef={id} />
      </>
    );

  return (
    <Column
      sortable={sortable}
      key={field}
      field={field}
      sortField={sortField}
      header={finalHeader}
      headerClassName={headerClassName}
      bodyClassName={bodyClassName}
      body={body}
      style={style}
      hidden={hidden}
    />
  );
};

export const modelTemplate = (rowData: LinksData) => {
  if (!rowData.models || rowData.models.length === 0) {
    return null;
  }

  const moreName = `tooltip-more-${uuidv4()}`;
  return (
    <div className="tags">
      {rowData.models.slice(0, 4).map((model, index) => {
        const icon = getMonitorTypeIcon(model);
        if (!icon) return null;

        const name = `tooltip-${uuidv4()}-${model}`;

        return (
          <React.Fragment key={model || index}>
            <span id={name} className={`${icon} ico--md`} />
            <Tooltip
              target={`#${name}`}
              className="tooltip--primary tooltip--top"
              position="top"
              showDelay={5}
            >
              {getMonitorName(model)}
            </Tooltip>
          </React.Fragment>
        );
      })}

      {rowData.models.length > 4 && (
        <React.Fragment>
          <div id={moreName} className="more-models">
            +{rowData.models.length - 4}
          </div>
          <Tooltip
            target={`#${moreName}`}
            className="tooltip--primary tooltip--top"
            position="top"
            showDelay={5}
          >
            {rowData.models
              .slice(4)
              .map((m) => getMonitorName(m))
              .join(', ')}
          </Tooltip>
        </React.Fragment>
      )}
    </div>
  );
};

export const sanitizeId = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .replaceAll(' ', '_')
    .replace(/[^a-z0-9_-]/g, '');

export const keywordsTemplate = (
  keywords: string[],
  visibleElements = 2,
  customTagStyle = 'tag tag--second tag--short',
  tooltipStyle = 'tooltip--primary'
) => {
  if (!keywords || keywords.length === 0) {
    return '-';
  }

  const moreName = `tooltip-more-${uuidv4()}`;
  return (
    <div className="tags tags--standard-gap">
      {keywords.slice(0, visibleElements).map((keyword, index) => {
        const safeName = sanitizeId(keyword);
        const keyName = safeName || index;
        const name = `tooltip-${uuidv4()}-${safeName}`;

        return (
          <React.Fragment key={keyName || index}>
            <span className={customTagStyle}>{keyword}</span>
            <Tooltip
              target={`#${name}`}
              className="tooltip--primary tooltip--top"
              position="top"
              showDelay={5}
            >
              {keyword}
            </Tooltip>
          </React.Fragment>
        );
      })}

      {keywords.length > visibleElements && (
        <React.Fragment>
          <div id={moreName} className="more-models">
            +{keywords.length - visibleElements}
          </div>
          <Tooltip
            target={`#${moreName}`}
            className={`${tooltipStyle} tooltip--top`}
            position="top"
            showDelay={5}
          >
            {keywords.slice(visibleElements).join(', ')}
          </Tooltip>
        </React.Fragment>
      )}
    </div>
  );
};
